import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiChevronLeft, FiChevronRight, FiFile, FiFileText, FiMusic } from 'react-icons/fi';
import * as S from '../styles';
// ----------------------

const obterUrlArquivo = (arquivo) => (
  /^https?:\/\//i.test(arquivo)
    ? arquivo
    : `https://rbento-web.s3.us-east-1.amazonaws.com/${arquivo.replace(/^\/+/, '').split('/').map(encodeURIComponent).join('/')}`
);

const obterTipoArquivo = (arquivo) => {
  const nome = arquivo.split('/').pop() || arquivo;
  if (/\.(mp4|webm|mov|m4v)$/i.test(nome)) return 'video';
  if (/\.(aac|amr|m4a|mp3|oga|ogg|opus|wav)$/i.test(nome)) return 'audio';
  if (/\.pdf$/i.test(nome)) return 'pdf';
  return 'imagem';
};

const estiloSeta = (lado) => ({
  position: 'absolute',
  top: '50%',
  [lado]: '16px',
  transform: 'translateY(-50%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '44px',
  height: '44px',
  border: 'none',
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.9)',
  color: '#111827',
  cursor: 'pointer',
});
// ----------------------

export default function ModalImg({ aberto, onFechar, arquivos = [], titulo = 'Arquivos salvos', id = 'evento' }) {
  const [indiceVisualizado, setIndiceVisualizado] = useState(null);

  const lista = Array.isArray(arquivos)
    ? arquivos.filter((arquivo) => typeof arquivo === 'string' && arquivo.trim())
    : [];
  const midias = lista.filter((arquivo) => ['imagem', 'video'].includes(obterTipoArquivo(arquivo)));
  const totalMidias = midias.length;
  const visualizando = indiceVisualizado !== null;

  const navegarMidia = (direcao) => {
    setIndiceVisualizado((atual) => (
      atual === null || totalMidias === 0 ? atual : (atual + direcao + totalMidias) % totalMidias
    ));
  };

  useEffect(() => {
    if (!visualizando) return undefined;

    const aoPressionarTecla = (teclaEvent) => {
      if (teclaEvent.key === 'ArrowRight') navegarMidia(1);
      if (teclaEvent.key === 'ArrowLeft') navegarMidia(-1);
    };

    window.addEventListener('keydown', aoPressionarTecla);
    return () => window.removeEventListener('keydown', aoPressionarTecla);
  }, [visualizando, totalMidias]);

  useEffect(() => {
    if (!aberto) setIndiceVisualizado(null);
  }, [aberto]);

  if (!aberto) return null;

  const midiaAtual = visualizando ? midias[indiceVisualizado] : null;
  const urlMidiaAtual = midiaAtual ? obterUrlArquivo(midiaAtual) : '';
  const parar = (clickEvent) => clickEvent.stopPropagation();

  // Portal fora do card evita o transform de hover e a propagação de cliques para o card
  return createPortal(
    <>
      <S.Overlay onClick={parar}>
        <S.ModalCard
          role="dialog"
          aria-modal="true"
          aria-labelledby={`arquivos-titulo-${id}`}
          onClick={parar}
        >
          <S.ModalHeader>
            <span id={`arquivos-titulo-${id}`}>{titulo}</span>
            <button
              type="button"
              onClick={(clickEvent) => {
                parar(clickEvent);
                if (onFechar) onFechar();
              }}
              aria-label="Fechar"
            >
              ×
            </button>
          </S.ModalHeader>
          <S.ModalBody>
            <S.FileGrid>
              {lista.map((arquivo, index) => {
                const nomeArquivo = arquivo.split('/').pop() || arquivo;
                const urlArquivo = obterUrlArquivo(arquivo);
                const tipo = obterTipoArquivo(arquivo);

                return (
                  <S.FileLink
                    key={`${arquivo}-${index}`}
                    href={urlArquivo}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Abrir ${nomeArquivo}`}
                    onClick={(clickEvent) => {
                      parar(clickEvent);
                      if (tipo === 'imagem' || tipo === 'video') {
                        clickEvent.preventDefault();
                        setIndiceVisualizado(midias.indexOf(arquivo));
                      }
                    }}
                  >
                    <S.FilePreview>
                      {tipo === 'imagem' ? (
                        <img src={urlArquivo} alt="" loading="lazy" />
                      ) : tipo === 'video' ? (
                        <video src={urlArquivo} muted preload="metadata" aria-label={`Prévia de ${nomeArquivo}`} />
                      ) : (
                        <S.FilePreviewFallback>
                          {tipo === 'pdf' ? <FiFileText size={30} /> : tipo === 'audio' ? <FiMusic size={30} /> : <FiFile size={30} />}
                          <span>{tipo === 'pdf' ? 'PDF' : 'ÁUDIO'}</span>
                        </S.FilePreviewFallback>
                      )}
                    </S.FilePreview>
                    <S.FileName>{nomeArquivo}</S.FileName>
                  </S.FileLink>
                );
              })}
            </S.FileGrid>
          </S.ModalBody>
        </S.ModalCard>
      </S.Overlay>

      {midiaAtual && (
        <S.Overlay style={{ zIndex: 1100, flexDirection: 'column', gap: '16px' }} onClick={parar}>
          {totalMidias > 1 && (
            <button
              type="button"
              aria-label="Arquivo anterior"
              title="Anterior"
              style={estiloSeta('left')}
              onClick={(clickEvent) => {
                parar(clickEvent);
                navegarMidia(-1);
              }}
            >
              <FiChevronLeft size={26} />
            </button>
          )}
          {obterTipoArquivo(midiaAtual) === 'video' ? (
            <video
              key={urlMidiaAtual}
              src={urlMidiaAtual}
              controls
              autoPlay
              style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: '10px' }}
            />
          ) : (
            <img
              key={urlMidiaAtual}
              src={urlMidiaAtual}
              alt={midiaAtual.split('/').pop()}
              style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: '10px', objectFit: 'contain' }}
            />
          )}
          {totalMidias > 1 && (
            <>
              <button
                type="button"
                aria-label="Próximo arquivo"
                title="Próximo"
                style={estiloSeta('right')}
                onClick={(clickEvent) => {
                  parar(clickEvent);
                  navegarMidia(1);
                }}
              >
                <FiChevronRight size={26} />
              </button>
              <span style={{ color: '#fff', fontWeight: 'bold' }}>{indiceVisualizado + 1} / {totalMidias}</span>
            </>
          )}
          <button
            type="button"
            onClick={(clickEvent) => {
              parar(clickEvent);
              setIndiceVisualizado(null);
            }}
            style={{ padding: '10px 30px', fontWeight: 'bold', backgroundColor: '#f0433d', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
          >
            X FECHAR
          </button>
        </S.Overlay>
      )}
    </>,
    document.body
  );
}
